<template>
  <div class="line-chart-container">
    <canvas :id="canvasId"></canvas>
  </div>
</template>

<script>
import Chart from 'chart.js/auto'

export default {
  name: 'LineChart',
  props: {
    chartId: {
      type: String,
      default: () => 'line-chart-' + Math.random().toString(36).substr(2, 9)
    },
    chartData: {
      type: Object,
      required: true,
      default: () => ({
        labels: [],
        datasets: []
      })
    },
    chartOptions: {
      type: Object,
      default: () => ({})
    },
    width: {
      type: Number,
      default: 400
    },
    height: {
      type: Number,
      default: 400
    }
  },
  data () {
    return {
      chart: null,
      uniqueId: 'line-chart-' + Date.now() + '-' + Math.random().toString(36).substr(2, 9),
      defaultOptions: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: {
          mode: 'index',
          intersect: false
        },
        plugins: {
          legend: {
            display: true,
            position: 'top',
            labels: {
              usePointStyle: true,
              padding: 15,
              font: {
                size: 12
              }
            }
          },
          tooltip: {
            enabled: true,
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            titleColor: '#fff',
            bodyColor: '#fff',
            borderColor: '#ddd',
            borderWidth: 1,
            titleFont: {
              size: 14,
              weight: 'bold'
            },
            bodyFont: {
              size: 13
            },
            padding: 10,
            cornerRadius: 4,
            displayColors: true,
            callbacks: {
              label: function (context) {
                let label = context.dataset.label || ''
                if (label) {
                  label += ': '
                }
                if (context.parsed.y !== null) {
                  label += context.parsed.y.toLocaleString()
                }
                return label
              }
            }
          }
        },
        scales: {
          x: {
            display: true,
            grid: {
              display: false,
              drawBorder: false
            },
            ticks: {
              color: '#606266',
              font: {
                size: 11
              }
            }
          },
          y: {
            display: true,
            grid: {
              borderDash: [2, 4],
              color: '#E4E7ED',
              drawBorder: false
            },
            ticks: {
              color: '#606266',
              font: {
                size: 11
              },
              callback: function (value) {
                return value.toLocaleString()
              }
            }
          }
        },
        elements: {
          line: {
            tension: 0.4,
            borderWidth: 2,
            borderJoinStyle: 'round'
          },
          point: {
            radius: 3,
            hitRadius: 10,
            hoverRadius: 5,
            hoverBorderWidth: 2
          }
        }
      }
    }
  },
  computed: {
    mergedOptions () {
      return this.deepMerge(this.defaultOptions, this.chartOptions)
    },
    canvasId () {
      return this.chartId || this.uniqueId
    }
  },
  watch: {
    chartData: {
      deep: true,
      handler () {
        this.updateChart()
      }
    },
    chartOptions: {
      deep: true,
      handler () {
        this.updateChart()
      }
    }
  },
  mounted () {
    this.renderChart()
  },
  beforeDestroy () {
    if (this.chart) {
      this.chart.destroy()
    }
  },
  methods: {
    renderChart () {
      this.$nextTick(() => {
        const ctx = document.getElementById(this.canvasId)
        if (!ctx) return

        // Check if a chart already exists on this canvas
        const existingChart = Chart.getChart(ctx)
        if (existingChart) {
          existingChart.destroy()
        }

        // Destroy our existing chart instance if it exists
        if (this.chart) {
          this.chart.destroy()
          this.chart = null
        }

        // Create new chart
        try {
          this.chart = new Chart(ctx, {
            type: 'line',
            data: this.chartData,
            options: this.mergedOptions
          })
        } catch (error) {
          console.error('Error creating line chart:', error)
        }
      })
    },
    updateChart () {
      if (!this.chart) {
        this.renderChart()
        return
      }

      // Update chart data
      this.chart.data = this.chartData
      this.chart.options = this.mergedOptions
      this.chart.update('active')
    },
    deepMerge (target, source) {
      const output = Object.assign({}, target)
      if (this.isObject(target) && this.isObject(source)) {
        Object.keys(source).forEach(key => {
          if (this.isObject(source[key])) {
            if (!(key in target)) {
              Object.assign(output, { [key]: source[key] })
            } else {
              output[key] = this.deepMerge(target[key], source[key])
            }
          } else {
            Object.assign(output, { [key]: source[key] })
          }
        })
      }
      return output
    },
    isObject (item) {
      return item && typeof item === 'object' && !Array.isArray(item)
    }
  }
}
</script>

<style scoped>
.line-chart-container {
  position: relative;
  width: 100%;
  height: 100%;
}

canvas {
  max-width: 100%;
  max-height: 100%;
}
</style>
